import type { Step } from '../lib/steps';

export type { Step };

const STEPS: { id: Step; label: string }[] = [
  { id: 'form', label: 'Your details' },
  { id: 'confirm', label: 'Confirm' },
  { id: 'avcheck', label: 'Equipment & join' },
];

function indexFor(step: Step) {
  return STEPS.findIndex(s => s.id === step);
}

export default function Stepper({
  current,
  currentComplete = false,
  onNavigate,
}: {
  current: Step;
  currentComplete?: boolean;
  onNavigate?: (step: Step) => void;
}) {
  const currentIdx = indexFor(current);

  return (
    <ol aria-label="Progress" className="flex flex-row items-start gap-0 list-none p-0 m-0 w-full">
      {STEPS.map((step, i) => {
        const isDone = i < currentIdx || (i === currentIdx && currentComplete);
        const isActive = i === currentIdx && !currentComplete;
        const isLast = i === STEPS.length - 1;

        const leftLineClass =
          i === 0 ? 'bg-transparent' : isDone || currentIdx >= i ? 'bg-nhs-green' : 'bg-[#e0e6e8]';
        const rightLineClass = isLast
          ? 'bg-transparent'
          : i < currentIdx || (i === currentIdx && currentComplete)
            ? 'bg-nhs-green'
            : 'bg-[#e0e6e8]';

        const labelVisible = (
          <>
            <span className="sr-only">
              {isDone ? 'Completed: ' : isActive ? 'Current step: ' : ''}
            </span>
            {step.label}
          </>
        );

        return (
          <li
            key={step.id}
            aria-current={isActive ? 'step' : undefined}
            className="flex flex-col items-center flex-1 min-w-0"
          >
            <div className="flex w-full items-center mb-2">
              <span aria-hidden="true" className={`h-[2px] flex-1 rounded-sm ${leftLineClass}`} />
              <span
                aria-hidden="true"
                className={[
                  'flex-none inline-flex items-center justify-center rounded-full font-bold text-sm w-7 h-7 mx-0',
                  isDone
                    ? 'bg-nhs-green text-white'
                    : isActive
                      ? 'bg-nhs-blue text-white'
                      : 'bg-white text-text-muted border border-border-input',
                ].join(' ')}
              >
                {isDone ? (
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  i + 1
                )}
              </span>
              <span aria-hidden="true" className={`h-[2px] flex-1 rounded-sm ${rightLineClass}`} />
            </div>
            <div className="w-full px-0.5 text-center whitespace-normal text-xs sm:text-sm leading-tight">
              {isDone && onNavigate ? (
                <button
                  type="button"
                  onClick={() => onNavigate(step.id)}
                  className={[
                    'max-w-full font-medium text-text-muted hover:text-nhs-blue hover:underline underline-offset-2',
                    'bg-transparent border-0 cursor-pointer p-0 text-center',
                  ].join(' ')}
                  aria-label={`Go back to: ${step.label}`}
                >
                  {labelVisible}
                </button>
              ) : (
                <span
                  className={[
                    'block max-w-full',
                    isActive ? 'text-text font-bold' : 'text-text-muted font-medium',
                  ].join(' ')}
                >
                  {labelVisible}
                </span>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
